"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils";

function Select({ ...props }) {
  return <SelectPrimitive.Root {...props} />;
}

function SelectTrigger({ className, children, ...props }) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm",
        "border-gray-300 placeholder:text-gray-400",
        "focus:outline-none focus:ring-0 focus:ring-gray-300",
        "hover:border-gray-600 transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectValue({ className, ...props }) {
  return (
    <SelectPrimitive.Value
      className={cn(
        // Lighter placeholder appearance
        "data-[placeholder]:text-gray-400 data-[placeholder]:opacity-60",
        className
      )}
      {...props}
    />
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  side = "bottom",
  align = "start",
  sideOffset = 4,
  ...props
}) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          // Match trigger width via Radix CSS variable
          "z-50 w-[var(--radix-select-trigger-width)] max-h-[300px] overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className
        )}
        {...props}
      >
        <SelectPrimitive.Viewport className="w-full p-1 max-h-[300px]">
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({ className, children, ...props }) {
  return (
    <SelectPrimitive.Item
      className={cn(
        // Option row with hover beige, selected state and checkmark
        "relative flex w-full cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none",
        "hover:bg-[#F7F6F3] focus:bg-[#F7F6F3] focus:text-gray-900",
        "data-[state=checked]:bg-[#F1EEE8] data-[state=checked]:font-medium",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
